import { CommonModule } from "@angular/common";
import { Component, computed, inject, signal } from "@angular/core";
import { DocumentDto, DocumentService } from "@malirang/api-client";
import {
    DocumentEtcType,
    DocumentInServiceType,
    DocumentPreServiceType,
} from "@malirang/common";
import {
    Badge,
    Icon,
    ModalAdapter,
    ModalContainer,
    ModalHeader,
} from "@malirang/front-share";
import { saveAs } from "file-saver";
import JSZip from "jszip";
import { environment } from "../../../../../env/env";
import { DocumentEmailShareModal } from "../document-email-share/document-email-share.modal";
import {
    DocumentShareParentModal,
    DocumentShareParentModalProps,
} from "../document-share-parent/document-share-parent.modal";

declare global {
    interface Window {
        Kakao: any;
    }
}

export type KakaoShareContent = {
    objectType: string;
    content: {
        title: string;
        description: string;
        imageUrl: string;
        link: {
            mobileWebUrl: string;
            webUrl: string;
        };
    };
    itemContent?: {
        profileText: string;
        profileImageUrl: string;
        titleImageUrl: string;
        titleImageText: string;
        titleImageCategory: string;
        items: {
            item: string;
            itemOp: string;
        }[];
        sum: string;
        sumOp: string;
    };
    social?: {
        likeCount: number;
        commentCount: number;
        sharedCount: number;
    };
    buttons?: {
        title: string;
        link: {
            mobileWebUrl: string;
            webUrl: string;
        };
    }[];
};

declare const Kakao: any;

export type DocumentShareModalProps = {
    documents: DocumentDto[];
};

@Component({
    selector: "app-document-share",
    templateUrl: "./document-share.modal.html",
    styleUrls: ["./document-share.modal.css"],
    imports: [CommonModule, ModalHeader, ModalContainer, Icon, Badge],
})
export class DocumentShareModal extends ModalAdapter<DocumentShareModalProps> {
    readonly documentService = inject(DocumentService);

    loading = signal(false);
    documents = computed(() => this.props()?.documents);
    firstDocumentName = computed(() => {
        const firstDocument = this.documents()?.[0];

        if (firstDocument?.phase === "PRE_SERVICE") {
            return DocumentPreServiceType[firstDocument.preServiceType!];
        }

        if (firstDocument?.phase === "IN_SERVICE") {
            return DocumentInServiceType[firstDocument.inServiceType!];
        }

        if (firstDocument?.phase === "ETC") {
            return DocumentEtcType[firstDocument.etcType!];
        }

        return firstDocument?.template?.name || "문서";
    });

    includeFixDocuments = computed(() =>
        this.documents()?.some(
            (doc) =>
                doc.phase === "PRE_SERVICE" ||
                doc.phase === "IN_SERVICE" ||
                doc.phase === "ETC",
        ),
    );

    title = computed(() => {
        const documentCount = this.documents()?.length || 0;
        return documentCount > 1
            ? `${this.firstDocumentName()} 외 ${documentCount - 1}건`
            : `${this.firstDocumentName()}`;
    });

    isShared = computed(() =>
        this.documents()?.some((doc) => (doc.sharedUsers?.length || 0) > 0),
    );

    async handleDownload() {
        const zip = new JSZip();
        this.loading.set(true);

        for (const doc of this.documents() || []) {
            const documentId = doc.id;
            const url = `${window.location.origin}/document-box/pdf?documentId=${documentId}`;

            try {
                const res = await fetch(
                    `${environment.baseUrl}/api/pdf?url=${encodeURIComponent(url)}`,
                );
                const blob = await res.blob();

                const fileName = `${this.firstDocumentName()}.pdf`;

                zip.file(fileName, blob);
                //
                this.loading.set(false);
            } catch (error) {
                this.loading.set(false);
            }
        }

        // zip 파일 생성
        const content = await zip.generateAsync({ type: "blob" });

        // 다운로드 실행
        const documentCount = this.documents()?.length || 0;
        saveAs(
            content,
            documentCount > 1
                ? `${this.firstDocumentName()} 외 ${documentCount - 1}건.zip`
                : `${this.firstDocumentName()}.zip`,
        );
    }

    async handleKakaoShare() {
        if (!Kakao.isInitialized()) {
            Kakao.init(environment.kakao.clientId);
        }

        const kakao = window.Kakao;
        const contents = [];

        for (const doc of this.documents() || []) {
            const url = `${window.location.origin}/document-box/pdf?documentId=${doc.id}`;

            try {
                const res = await fetch(
                    `${environment.baseUrl}/api/pdf/generate-url?url=${encodeURIComponent(url)}`,
                );
                const data = await res.json();
                const downloadUrl = data.url;

                const content = {
                    title: this.firstDocumentName(),
                    description: "말이랑에서 공유한 문서입니다.",
                    imageUrl: "https://malirang.com/logo.svg",
                    link: {
                        mobileWebUrl: downloadUrl,
                        webUrl: downloadUrl,
                    },
                };

                contents.push(content);
                this.loading.set(false);
            } catch (error) {
                this.loading.set(false);
            }
        }

        if (contents.length > 1) {
            kakao.Share.sendDefault({
                objectType: "list",
                headerTitle: "말이랑 치료문서 공유",
                headerLink: {
                    webUrl: `${window.location.origin}`,
                    mobileWebUrl: `${window.location.origin}`,
                },
                contents,
            });
        } else {
            const content = contents[0];
            kakao.Share.sendDefault({
                objectType: "feed",
                content: {
                    title: content.title,
                    description: content.description,
                    imageUrl: content.imageUrl,
                    link: {
                        mobileWebUrl: content.link.mobileWebUrl,
                        webUrl: content.link.webUrl,
                    },
                },
            });
        }
    }

    handleMailShare() {
        const modal = this.modalService.create(DocumentEmailShareModal);

        modal.closed.subscribe({
            next: (emails) => {
                if (emails && emails.length) {
                    this.loading.set(true);

                    const documentUrls = this.documents()?.map((doc) => {
                        return `${window.location.origin}/document-box/pdf?documentId=${doc.id}`;
                    });

                    this.documentService
                        .documentControllerEmailShare({
                            body: {
                                documentUrls: documentUrls || [],
                                emails,
                            },
                        })
                        .subscribe({
                            next: () => {
                                this.toastService.success(
                                    "이메일 공유가 완료되었습니다.",
                                );
                                this.close();
                                this.loading.set(false);
                            },
                            error: () => {
                                this.loading.set(false);
                            },
                        });
                }
            },
        });
    }

    handleParentShare() {
        const documents = this.documents() || [];

        const modal = this.modalService.create<DocumentShareParentModalProps>(
            DocumentShareParentModal,
            {
                componentProps: {
                    documents,
                },
            },
        );

        modal.closed.subscribe({
            next: (result) => {
                if (result) {
                    this.close(result);
                }
            },
        });
    }
}
